import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { useProjectContext } from '@/project/ProjectContext';
import { ProjectGate } from '@/features/projects/ProjectGate';
import { Button } from '@/components/ui/Button';
import { LoadingState, StateMessage } from '@/components/StateMessage/StateMessage';
import styles from '@/features/traces/traces.module.css';

type HubTraceNode = {
  node_type: string;
  id: string;
  at: string;
  label: string;
  status?: string;
  detail?: Record<string, unknown>;
};

export function TracePage() {
  const { traceId } = useParams<{ traceId: string }>();
  const { selectedProject } = useProjectContext();
  const projectId = selectedProject?.id;
  const [devMode, setDevMode] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const traceQ = useQuery({
    queryKey: ['hub-trace', projectId, traceId],
    enabled: Boolean(projectId && traceId),
    queryFn: () =>
      apiFetch<{ trace_id: string; nodes: HubTraceNode[] }>(
        `/api/hub-traces/${traceId}?project_id=${projectId}`,
      ),
  });

  async function copyTraceId() {
    if (!traceId) return;
    await navigator.clipboard.writeText(traceId);
  }

  return (
    <ProjectGate>
      <div className={styles.page}>
        {!projectId ? (
          <StateMessage title="Kies een project" />
        ) : !traceId ? (
          <StateMessage title="Geen trace-id" />
        ) : traceQ.isLoading ? (
          <LoadingState label="Trace laden…" />
        ) : traceQ.isError ? (
          <StateMessage title="Trace niet gevonden" message="Controleer project en trace-id." />
        ) : (
          <>
            <div className={styles.headerRow}>
              <div>
                <Link to="/app/automations">← Automations</Link>
                <h1 style={{ margin: '0.5rem 0 0' }}>Hub trace</h1>
                <p className={styles.traceId}>{traceQ.data!.trace_id}</p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <Button type="button" variant="secondary" onClick={() => void copyTraceId()}>
                  Kopieer trace_id
                </Button>
                <Button type="button" variant="ghost" onClick={() => setDevMode((v) => !v)}>
                  {devMode ? 'Developer mode uit' : 'Developer mode'}
                </Button>
              </div>
            </div>

            <ul className={styles.timeline}>
              {(traceQ.data?.nodes ?? []).map((node) => (
                <li key={`${node.node_type}-${node.id}`} className={styles.timelineItem}>
                  <div className={styles.nodeLabel}>{node.label}</div>
                  <div className={styles.nodeMeta}>
                    {node.node_type} · {new Date(node.at).toLocaleString('nl-NL')}
                    {node.status ? ` · ${node.status}` : ''}
                  </div>
                  {devMode && node.detail ? (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() =>
                          setExpanded((prev) => ({ ...prev, [node.id]: !prev[node.id] }))
                        }
                      >
                        {expanded[node.id] ? 'JSON verbergen' : 'JSON tonen'}
                      </Button>
                      {expanded[node.id] ? (
                        <pre className={styles.detailPre}>{JSON.stringify(node.detail, null, 2)}</pre>
                      ) : null}
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
            {!traceQ.data?.nodes.length ? <p>Geen nodes voor deze trace.</p> : null}
          </>
        )}
      </div>
    </ProjectGate>
  );
}
